import type { EventRow, SeriesPoint } from "@/api/types";
import { Panel } from "@/components/ui";
import { useChartColors } from "@/lib/theme";
import { useECharts } from "@/lib/use-echarts";
import * as echarts from "echarts";
import type { EChartsOption } from "echarts";
import { useEffect, useState } from "react";

interface TipRevenueChartProps {
  series: SeriesPoint[] | null;
  events: EventRow[] | null;
  range?: string;
  loading?: boolean;
}

type Mode = "revenue" | "fee";
type Currency = "eth" | "usd";
type Bucket = "hour" | "day" | "week" | "month" | "quarter" | "year" | "cumulative";

const BUCKETS: { key: Bucket; label: string; title: string }[] = [
  { key: "hour", label: "H", title: "Hourly" },
  { key: "day", label: "D", title: "Daily" },
  { key: "week", label: "W", title: "Weekly" },
  { key: "month", label: "M", title: "Monthly" },
  { key: "quarter", label: "Q", title: "Quarterly" },
  { key: "year", label: "Y", title: "Yearly" },
  { key: "cumulative", label: "C", title: "Cumulative" },
];

function bucketKey(ts: string, bucket: Bucket): string {
  const d = new Date(ts);
  if (bucket === "hour")
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")} ${String(d.getUTCHours()).padStart(2, "0")}`;
  if (bucket === "day" || bucket === "cumulative")
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  if (bucket === "month")
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
  if (bucket === "quarter") return `${d.getUTCFullYear()}-Q${Math.ceil((d.getUTCMonth() + 1) / 3)}`;
  if (bucket === "year") return `${d.getUTCFullYear()}`;
  // ISO week
  const tmp = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const wk = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${tmp.getUTCFullYear()}-W${String(wk).padStart(2, "0")}`;
}

function aggregateSeries(data: SeriesPoint[], bucket: Bucket): SeriesPoint[] {
  const groups = new Map<string, SeriesPoint[]>();
  for (const p of data) {
    const k = bucketKey(p.ts, bucket);
    if (!groups.has(k)) groups.set(k, []);
    const arr = groups.get(k);
    if (arr) arr.push(p);
  }
  return Array.from(groups.values()).map((pts) => ({
    ts: pts[0].ts,
    tip_revenue_eth: pts.reduce((s, p) => s + p.tip_revenue_eth, 0),
    tip_revenue_usd: pts.reduce((s, p) => s + p.tip_revenue_usd, 0),
    top_fee_gwei: Math.max(...pts.map((p) => p.top_fee_gwei)),
    block_count: pts.reduce((s, p) => s + Number(p.block_count), 0),
    tipped_tx_count: pts.reduce((s, p) => s + Number(p.tipped_tx_count), 0),
    contested_share: pts.reduce((s, p) => s + p.contested_share, 0) / pts.length,
    hhi: Math.round(pts.reduce((s, p) => s + p.hhi, 0) / pts.length),
  }));
}

const EVENT_COLORS: Record<string, string> = {
  timeboost_launch: "#f59e0b",
  pga_activation: "#22c55e",
  sc_pause: "#ef4444",
};

function eventColor(id: string): string {
  return EVENT_COLORS[id] ?? "#6b7280";
}

export function TipRevenueChart({ series, events, loading = false }: TipRevenueChartProps) {
  const [mode, setMode] = useState<Mode>("revenue");
  const cc = useChartColors();
  const [currency, setCurrency] = useState<Currency>("eth");
  const [bucket, setBucket] = useState<Bucket>("hour");
  const [zoomRange, setZoomRange] = useState({ start: 0, end: 100 });

  const data = aggregateSeries(series ?? [], bucket);
  const xData = data.map((p) => p.ts);

  let yData: number[];
  if (mode === "fee") {
    yData = data.map((p) => p.top_fee_gwei);
  } else if (currency === "usd") {
    yData = data.map((p) => p.tip_revenue_usd);
  } else {
    yData = data.map((p) => p.tip_revenue_eth);
  }
  if (bucket === "cumulative") {
    let cum = 0;
    yData = yData.map((v) => {
      cum += v;
      return cum;
    });
  }

  const markLines =
    events?.map((ev) => ({
      xAxis: ev.event_date,
      lineStyle: { color: eventColor(ev.id), type: "dashed" as const, width: 1 },
      label: {
        show: true,
        formatter: ev.label,
        fontSize: 10,
        color: eventColor(ev.id),
        position: "insideEndTop" as const,
      },
    })) ?? [];

  const yFormatter = (v: number) => {
    if (mode === "fee") return `${v.toFixed(0)}`;
    if (currency === "usd") return `$${v.toFixed(2)}`;
    return `${v.toFixed(4)}`;
  };

  const tooltipFormatter = (params: unknown) => {
    const p = params as Array<{ name: string; value: number }>;
    if (!p[0]) return "";
    let val: string;
    if (mode === "fee") val = `${p[0].value.toFixed(2)} gwei`;
    else if (currency === "usd") val = `$${p[0].value.toFixed(2)}`;
    else val = `${p[0].value.toFixed(6)} ETH`;
    return `${p[0].name}<br/>${val}`;
  };

  // Switch to bars when the visible window is narrow enough to show individual buckets clearly
  const visiblePct = zoomRange.end - zoomRange.start;
  const visibleCount =
    xData.length > 0 ? Math.max(1, Math.round((visiblePct / 100) * xData.length)) : 999;
  const useBar = visibleCount <= 60 && bucket !== "cumulative";

  const option: EChartsOption = {
    grid: { left: 56, right: 16, top: 32, bottom: 60, containLabel: false },
    dataZoom: [
      {
        type: "slider",
        xAxisIndex: 0,
        bottom: 4,
        height: 28,
        borderColor: "transparent",
        backgroundColor: "rgba(255,255,255,0.03)",
        fillerColor: "rgba(59,130,246,0.14)",
        handleStyle: { color: "#fff", borderColor: "#3b82f6", borderWidth: 1.5 },
        handleSize: "80%",
        moveHandleStyle: { color: "#3b82f6" },
        dataBackground: {
          lineStyle: { color: "#3b82f6", width: 1, opacity: 0.35 },
          areaStyle: { color: "rgba(59,130,246,0.08)", opacity: 1 },
        },
        selectedDataBackground: {
          lineStyle: { color: "#3b82f6", width: 1.5 },
          areaStyle: { color: "rgba(59,130,246,0.18)" },
        },
        textStyle: { color: "transparent", fontSize: 0 },
        emphasis: { handleLabel: { show: false }, handleStyle: { borderColor: "#60a5fa" } },
      },
      { type: "inside", xAxisIndex: 0 },
    ],
    xAxis: {
      type: "category",
      data: xData,
      axisLabel: {
        fontSize: 10,
        color: cc.text,
        formatter: (v: string) => {
          const d = new Date(v);
          if (bucket === "hour")
            return `${d.getUTCMonth() + 1}/${d.getUTCDate()} ${String(d.getUTCHours()).padStart(2, "0")}h`;
          if (bucket === "year") return `${d.getUTCFullYear()}`;
          if (bucket === "quarter")
            return `${d.getUTCFullYear()} Q${Math.ceil((d.getUTCMonth() + 1) / 3)}`;
          if (bucket === "month")
            return `${d.getUTCFullYear()}/${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
          if (bucket === "week") {
            const tmp = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
            const day = tmp.getUTCDay() || 7;
            tmp.setUTCDate(tmp.getUTCDate() + 4 - day);
            const wk = Math.ceil(
              ((tmp.getTime() - new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1)).getTime()) /
                86_400_000 +
                1) /
                7,
            );
            return `W${String(wk).padStart(2, "0")}`;
          }
          return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
        },
      },
      axisLine: { lineStyle: { color: cc.grid } },
      axisTick: { show: false },
    },
    yAxis: {
      type: "value",
      axisLabel: { fontSize: 10, color: cc.text, formatter: yFormatter },
      splitLine: { lineStyle: { color: cc.grid } },
    },
    tooltip: { trigger: "axis", formatter: tooltipFormatter },
    series: [
      useBar
        ? {
            type: "bar",
            data: yData,
            barMaxWidth: 20,
            itemStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: "#3b82f6" },
                  { offset: 1, color: "rgba(59,130,246,0.35)" },
                ],
              },
              borderRadius: [2, 2, 0, 0],
            },
            markLine:
              markLines.length > 0 ? { silent: true, data: markLines, symbol: "none" } : undefined,
          }
        : {
            type: "line",
            data: yData,
            smooth: false,
            symbol: "none",
            lineStyle: { color: "#3b82f6", width: 1.5 },
            areaStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: "rgba(59,130,246,0.25)" },
                  { offset: 1, color: "rgba(59,130,246,0)" },
                ],
              },
            },
            markLine:
              markLines.length > 0 ? { silent: true, data: markLines, symbol: "none" } : undefined,
          },
    ],
  };

  const containerRef = useECharts(option, [
    mode,
    currency,
    bucket,
    series,
    events,
    cc.grid,
    useBar,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: containerRef.current is a stable DOM ref set by useECharts; we intentionally run once on mount
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = echarts.getInstanceByDom(containerRef.current);
    if (!chart) return;
    const handler = (params: unknown) => {
      const p = params as {
        start?: number;
        end?: number;
        batch?: Array<{ start: number; end: number }>;
      };
      const start = p.start ?? p.batch?.[0]?.start ?? 0;
      const end = p.end ?? p.batch?.[0]?.end ?? 100;
      setZoomRange({ start, end });
    };
    chart.on("datazoom", handler);
    return () => {
      chart.off("datazoom", handler);
    };
  }, []);

  const btnStyle = (active: boolean): React.CSSProperties => ({
    padding: "2px 8px",
    fontSize: 11,
    background: active ? "var(--color-accent)" : "transparent",
    color: active ? "#fff" : "var(--color-text-muted)",
    border: "1px solid",
    borderColor: active ? "var(--color-accent)" : "var(--color-border)",
    borderRadius: "var(--radius-badge)",
    cursor: "pointer",
  });

  return (
    <Panel
      title="TIP REVENUE"
      action={
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {mode === "revenue" && (
            <>
              <button
                type="button"
                style={btnStyle(currency === "eth")}
                onClick={() => setCurrency("eth")}
              >
                ETH
              </button>
              <button
                type="button"
                style={btnStyle(currency === "usd")}
                onClick={() => setCurrency("usd")}
              >
                USD
              </button>
              <span style={{ width: 1, background: "var(--color-border)", margin: "2px 2px" }} />
            </>
          )}
          <button
            type="button"
            style={btnStyle(mode === "revenue")}
            onClick={() => setMode("revenue")}
          >
            Revenue
          </button>
          <button type="button" style={btnStyle(mode === "fee")} onClick={() => setMode("fee")}>
            Top Fee
          </button>
          <>
            <span style={{ width: 1, background: "var(--color-border)", margin: "2px 2px" }} />
            {BUCKETS.map((b) => (
              <button
                key={b.key}
                type="button"
                title={b.title}
                style={btnStyle(bucket === b.key)}
                onClick={() => setBucket(b.key)}
              >
                {b.label}
              </button>
            ))}
          </>
        </div>
      }
    >
      {loading ? (
        <div className="skeleton" style={{ height: 220, borderRadius: "var(--radius-panel)" }} />
      ) : (
        <div ref={containerRef} style={{ height: 220 }} />
      )}
    </Panel>
  );
}
